import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Globe, Trophy, Users, Hash, Target, GraduationCap, Eye } from 'lucide-react';
import { Club } from '@/hooks/useClubs';
import { useUserTeams } from '@/hooks/useUserTeams';
import { CurrentPlayerDetails, useCurrentPlayerDetailsData } from './CurrentPlayerDetails';

interface ClubCardProps {
  club: Club;
}

// Team card component to handle individual team display and click
interface Team {
  id: string;
  name: string;
  division_id?: string;
  birth_date_cutoff?: string;
  profile_picture_url?: string;
  divisions?: {
    name: string;
  };
}

const TeamCard = ({ team, navigate }: { team: Team, navigate: (path: string) => void }) => {
  const { player, loading } = useCurrentPlayerDetailsData(team.id);
  
  const handleTeamClick = () => {
    if (player) {
      navigate(`/player/${player.id}/programs`);
    }
  };

  const isClickable = !loading && player;

  return (
    <div 
      key={team.id} 
      className={`space-y-2 ${isClickable ? 'cursor-pointer hover:bg-muted/30 rounded p-2 -m-2 transition-colors' : ''}`}
      onClick={isClickable ? handleTeamClick : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="font-bold text-sm">{team.name}</span>
        {team.divisions?.name && (
          <Badge variant="outline" className="text-xs px-1.5 py-0.5">
            {team.divisions.name}
          </Badge>
        )}
      </div>
      <CurrentPlayerDetails teamId={team.id} teamName={team.name} />
    </div>
  );
};

export const ClubCard = ({ club }: ClubCardProps) => {
  const { teams, loading: teamsLoading } = useUserTeams(club.id);
  const navigate = useNavigate();
  
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'destructive';
      case 'coach':
        return 'default';
      case 'parent':
        return 'secondary';
      case 'player':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'coach':
        return 'Coach';
      case 'parent':
        return 'Parent';
      case 'player':
        return 'Player';
      default:
        return role;
    }
  };

  return (
    <Card 
      className="h-full border-2"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {club.profile_picture_url ? (
              <img
                src={club.profile_picture_url}
                alt={`${club.name} logo`}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                <Trophy className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-lg leading-tight">{club.name}</h3>
              <p className="text-sm text-muted-foreground">{club.organization_name}</p>
            </div>
          </div>
          <Badge variant={getRoleBadgeVariant(club.user_role)}>
            {getRoleLabel(club.user_role)}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          {club.sport && (
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{club.sport}</span>
            </div>
          )}
          
          {club.location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{club.location}</span>
            </div>
          )}
          
          {club.website && (
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <a
                href={club.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Visit Website
              </a>
            </div>
          )}
        </div>
        
        {/* Teams Information Card */}
        <div className="mt-4">
          <Card className="bg-muted/50 border-muted">
            <CardContent className="pt-4">
              {teamsLoading ? (
                <p className="text-xs text-muted-foreground">Loading teams...</p>
              ) : teams.length === 0 ? (
                <p className="text-xs text-muted-foreground">No teams assigned</p>
              ) : (
                  <div className="space-y-3">
                    {teams.slice(0, 2).map((team) => (
                      <TeamCard key={team.id} team={team} navigate={navigate} />
                    ))}
                   {teams.length > 2 && (
                     <p className="text-xs text-muted-foreground pt-1">
                       +{teams.length - 2} more teams
                     </p>
                   )}
                 </div>
              )}
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};